
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);

        // Check if this is a chunk load error (common in Vite/React after new deployment)
        const isChunkError =
            error.name === 'ChunkLoadError' ||
            error.message.includes('Failed to fetch dynamically imported module') ||
            error.message.includes('Loading chunk');

        if (isChunkError) {
            console.warn('Chunk load error detected. This usually happens after a new deployment.');
            // Only auto-reload once to avoid infinite loops
            const hasReloaded = sessionStorage.getItem('chunk_error_reload');
            if (!hasReloaded) {
                sessionStorage.setItem('chunk_error_reload', 'true');
                window.location.reload();
            }
        }
    }

    public render() {
        if (this.state.hasError) {
            const error = this.state.error;
            const isChunkError = error && (
                error.name === 'ChunkLoadError' ||
                error.message.includes('Failed to fetch dynamically imported module') ||
                error.message.includes('Loading chunk')
            );

            return this.props.fallback || (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                    <div className="text-center max-w-md">
                        <div className="mb-6 inline-flex items-center justify-center w-16 h-16 bg-red-100 text-red-600 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {isChunkError ? 'Update Available' : 'Something went wrong'}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {isChunkError
                                ? 'A new version of the application is available. Please refresh to continue.'
                                : 'We apologize for the inconvenience. Please try refreshing the page.'}
                        </p>
                        <button
                            onClick={() => {
                                // Clear the reload flag when manually refreshing
                                sessionStorage.removeItem('chunk_error_reload');
                                window.location.reload();
                            }}
                            className="bg-primary hover:opacity-90 text-white font-medium py-2 px-8 rounded-lg transition-all shadow-md"
                        >
                            {isChunkError ? 'Update Now' : 'Refresh Page'}
                        </button>

                        {import.meta.env.DEV && this.state.error && (
                            <div className="mt-8 p-4 bg-red-50 text-red-700 text-left rounded overflow-auto max-w-2xl mx-auto text-sm font-mono border border-red-100">
                                {this.state.error.toString()}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
